import { Button, Divider, TextInput } from '@mantine/core';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'wouter';
import { LegalFooter } from './components/LegalFooter';
import { Page } from './components/Page';
import { PageCard } from './components/PageCard';
import { PageCardLogo } from './components/PageCardLogo';
import { SocialLoginView } from './components/SocialLoginView';
import { SplashPane } from './components/SplashPane';
import { Colors } from './patterns/Colors';

const useDirectInviteRegisterMutation = (inviteId: string) =>
    useMutation({
        mutationFn: async (trimmedName: string) => {
            // The token from the invitation email proves the address; the server requires it
            const t = new URLSearchParams(window.location.search).get('t') ?? '';
            const res = await fetch(`/direct-invite/${inviteId}/register?t=${encodeURIComponent(t)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: trimmedName }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => null) as { message?: string } | null;
                throw new Error(body?.message ?? `Status ${res.status}`);
            }
            const { redirectTo } = await res.json() as { redirectTo: string };
            window.location.href = redirectTo;
            // Keep the mutation pending until the page unloads so the button
            // stays in its loading state through the redirect.
            await new Promise<never>(() => { });
        },
    });

export const DirectInvitePage = () => {

    const { inviteId } = useParams<{ inviteId: string }>();
    const callbackURL = `/direct-invite/${inviteId}`;

    const [name, setName] = useState('');
    const anyInFlight = useIsMutating() > 0;
    const registerMutation = useDirectInviteRegisterMutation(inviteId);

    const onRegister = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || registerMutation.isPending) return;
        registerMutation.mutate(name.trim());
    };

    return <Page>
        <PageCard splash={<SplashPane />}>
            <PageCardLogo />
            <Form onSubmit={onRegister}>
                <Heading>You've been invited to Spreo</Heading>
                <Subhead>Enter your name to create your account.</Subhead>
                <TextInput
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={e => setName(e.currentTarget.value)}
                    autoFocus
                    disabled={anyInFlight}
                    w="100%"
                    mb="md"
                />
                <Button
                    type="submit"
                    loading={registerMutation.isPending}
                    disabled={!name.trim() || anyInFlight}
                    color="primary"
                    size="sm"
                    fullWidth
                    mb="md"
                >
                    Continue
                </Button>
            </Form>
            <Divider my="md" label="or" labelPosition="center" />
            <SocialLoginView callbackURL={callbackURL} />
            {registerMutation.error && <ErrorText>{registerMutation.error.message}</ErrorText>}
            <LegalFooter />
        </PageCard>
    </Page>;
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Heading = styled.h1`
    font-size: 1.25rem;
    font-weight: 500;
    color: ${Colors.text};
    margin: 0 0 0.5rem;
`;

const Subhead = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    opacity: 0.7;
    margin: 0 0 1rem;
`;

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0.5rem 0 0;
`;
